import React from 'react'
import { soompi } from 'scripts/api/SoompiApi'
import locale, { getLang } from 'shared/i18n/react'
import Modal from 'scripts/lib/Modal'
import Login from 'scripts/lib/firebase/login'
import { eventTriggerGA } from 'scripts/lib/utils'
import { Helmet } from 'react-helmet'
import GenerateItem from 'scripts/components/embed/PollItem'
import Constants from 'scripts/base/Constants'

const nowPast = (end) => {
  const EndDate = new Date(end * 1000)
  const DateNow = new Date().getTime()
  return DateNow >= EndDate
}

class Poll extends React.Component {
  constructor(props) {
    super(props)
    const { id, layout = 'default', auth } = this.props
    this.state = {
      id,
      layout,
      auth,
      title: '',
      start: 0,
      end: 0,
      entries: [],
      hasFailed: false,
      cycleVotes: 0,
      cycleDuration: 0,
      selected: [],
      showResults: false,
      updating: false,
      voting: false,
      open: false,
      alert: {title: '', message: ''},
      hasEnded: false
    }
    this.lang = getLang()
    this.setCampaign()
    this.pollContainer = React.createRef()
  }

  close = () => {
    this.setState({open: false})
  }

  toggleShowResults = () => {
    const { showResults, updating } = this.state
    // scroll to the top of the poll container
    window && window.scrollTo(0, this.pollContainer.current.offsetTop)
    // check if going back to poll, no need to update just toggle
    if (showResults) {
      this.setState({showResults: !showResults})
      return
    }
    if (updating) { return } // check if we are already updating
    this.setState({updating: true})

    // fetch new updates could be delayed data due to cache
    this.getEntries({page: 1, start: true, sort: 'popularity'}).then(() => {
      this.setState({showResults: !showResults, updating: false})
    })
  }

  getEntries = ({page = 1, start = false, sort = 'title'}) => {
    return soompi.setLanguage(this.lang).request(`vote-campaigns/${this.state.id}/entries.json?perPage=50&page=${page}&sort=${sort}`, { method: 'get' }).then((res) => {
      const { results, pageInfo } = res.data
      const allEntries = start ? [] : this.state.entries
      this.setState({entries: [...allEntries, ...results]}, () => {
        if (pageInfo.hasNextPage) {
          this.getEntries({page: page + 1, sort}) // loop through the pages
        }
      })
    }).catch(e => {
      this.setState({hasFailed: true})
    })
  }

  setCampaign = () => {
    soompi.setLanguage(this.lang).request(`vote-campaigns/${this.state.id}.json`, { method: 'get' }).then((res) => {
      const {title, start, end, cycleVotes, cycleDuration} = res.data
      const hasEnded = nowPast(end)
      this.setState({ title, start, end, cycleVotes, cycleDuration, hasEnded }, () => {
        this.getEntries({page: 1, start: true, sort: (hasEnded ? 'popularity' : 'title')})
      })
    }).catch(e => {
      this.setState({hasFailed: true})
    })
  }

  shouldComponentUpdate(nextProps) {
    if (nextProps !== this.props) {
      this.setState({auth: nextProps.auth})
    }
    return true
  }

  selectItem = (id) => {
    let { selected, cycleVotes, auth } = this.state
    if (auth !== Constants.Auth.OK) {
      this.showLoginPrompt()
      return
    }

    // check if user clicks on the same item; then remove the vote
    if (selected.indexOf(id) !== -1) {
      selected.splice(selected.indexOf(id), 1)
    } else if (selected.length < cycleVotes) { // check for number of votes allowed per cycle
      selected.push(id)
    } else if (cycleVotes === 1) { // check if only 1 vote is allowed then remove and remplace it
      selected.shift()
      selected.push(id)
    }

    this.setState({selected})
  }

  isItemSelected = (itemId) => {
    return this.state.selected.indexOf(itemId) !== -1
  }

  // Tiled layout and submits all selection at once
  mapTiledEntries = (item, index) => {
    const { layout, showResults, hasEnded } = this.state
    const ShowPercent = () => showResults || hasEnded ? <span className="percent">{item.percent}%</span> : null
    const ShowProgress = () => showResults || hasEnded ? this.renderProgress(item.percent) : null
    return (
      <GenerateItem key={index} item={item} layout={layout} onClick={(itemId) => this.selectItem(itemId)} selected={this.isItemSelected(item.id)} hasEnded={hasEnded} showResults={showResults}>
        {item.metadata.image && <div className="soompi-poll-item-image"><img data-src={item.metadata.image} className="lazyload" /></div> }
        <div className="soompi-poll-item-title">
          <ShowPercent />
          <span className="soompi-poll-item-title-text">{item.title} {(showResults || hasEnded) && item.metadata.info1 && `- (${item.metadata.info1})`}</span>
        </div>
        <ShowProgress />
        {(!showResults && !hasEnded) && item.metadata.info1 && <div className="soompi-poll-item-info">{item.metadata.info1}</div>}
      </GenerateItem>
    )
  }

  // Simple poll type that allows one vote only
  // save indent to cookie if current user has voted
  mapEntries = (item, index) => {
    const { layout, showResults, hasEnded } = this.state
    const ShowPercent = () => showResults || hasEnded ? <span className="percent">{item.percent}%</span> : null
    const ShowProgress = () => showResults || hasEnded ? this.renderProgress(item.percent) : null
    return (
      <GenerateItem key={index} item={item} layout={layout} onClick={(itemId) => this.selectItem(itemId)} selected={this.isItemSelected(item.id)} hasEnded={hasEnded} showResults={showResults}>
        <div className="soompi-poll-item-title">
          <span>{item.title}</span>
          <ShowPercent />
        </div>
        <ShowProgress />
      </GenerateItem>
    )
  }

  renderProgress(percent) {
    return (
      <div className="progress-container">
        <div className="progress-bar" style={{width: `${percent}%`}}></div>
      </div>
    )
  }

  dataFetchFailed() {
    return (
      <div>Failed to fetch Poll.</div>
    )
  }

  dataInitialising() {
    return (
      <div>Initializing...</div>
    )
  }

  renderSubmitButton = () => {
    const {showResults, selected, hasEnded} = this.state
    const classDisabled = (selected.length === 0) ? 'disabled' : ''
    if (showResults || hasEnded) { return null }
    return (
      <div className="soompi-poll-navigation submit-button-container center-align">
        <a href="javascript:;" onClick={() => this.doSubmitVotes()} className={`btn btn-unicorn poll-submit-button ${classDisabled}`}>Submit Votes</a>
      </div>
    )
  }

  renderByType = () => {
    const {layout, entries} = this.state
    let renderedEntries = null

    switch (layout) {
      case 'tiled':
        renderedEntries = entries.map(this.mapTiledEntries)
        break
      default:
        renderedEntries = entries.map(this.mapEntries)
    }

    return (
      <ul className={`soompi-poll-items-container row`}>{renderedEntries}</ul>
    )
  }

  renderCounter = () => {
    const {selected, cycleVotes, showResults, hasEnded} = this.state
    if (cycleVotes <= 1 || showResults) { return null }
    if (hasEnded) {
      return (
        <React.Fragment>
          Campaign has ended
        </React.Fragment>
      )
    }
    return (
      <React.Fragment>
        {selected.length} of {cycleVotes} votes allowed
      </React.Fragment>
    )
  }

  showResultsButton = () => {
    const { showResults, hasEnded } = this.state
    if (hasEnded) { return null }
    return (
      <a className="show-results-button" href="javascript:;" onClick={() => this.toggleShowResults()}>{`${showResults ? 'Return to Poll' : 'View Results'}`}</a>
    )
  }

  showUpdating = () => {
    const {updating} = this.state
    if (!updating) { return null }
    return (
      <div className="updating-overlay">Updating...</div>
    )
  }

  doSubmitVotes = async () => {
    const {selected, id} = this.state

    this.setState({updating: true})
    for (const item of selected) {
      await soompi.setLanguage(this.lang).request(`users/poll/${id}/${item}`, { method: 'get' }, {apiEndpoint: ''})
    }
    this.setState({selected: [], updating: false})
    this.toggleShowResults()
  }

  setMessage = () => {
    return (
      <React.Fragment>
        <div>{locale.reactions.dialogText}</div>
        <Login toggleWithId={() => this.setState({open: false})} />
      </React.Fragment>
    )
  }

  showLoginPrompt = () => {
    this.setState({
      open: true,
      alert: {
        title: locale.reactions.dialogTitle,
        message: this.setMessage()
      }
    })
  }

  showMeta = () => {
    if (!this.props.embed) { return null }
    return (
      <Helmet onChangeClientState={eventTriggerGA}>
        <title defer={false}>Poll: {this.state.title} | Soompi</title>
      </Helmet>
    )
  }

  render() {
    const {hasFailed, entries, title, layout, selected, showResults} = this.state
    const ShowCounter = () => this.renderCounter()
    const ShowResults = () => this.showResultsButton()
    const SubmitVotes = () => this.renderSubmitButton()
    const UpdatingOverLay = () => this.showUpdating()
    const ShowMeta = () => this.showMeta()
    const stickyClass = selected.length !== 0 || showResults ? 'sticky' : ''

    if (hasFailed) {
      return this.dataFetchFailed()
    }

    if (!entries) {
      return this.dataInitialising()
    }

    return (
      <React.Fragment>
        <ShowMeta />
        <div className={`soompi-poll-container ${layout}`} ref={this.pollContainer}>
          <UpdatingOverLay />
          <div className={`soompi-poll-navigation-container nav-top ${stickyClass}`}>
            <h3 className="soompi-poll-title">Poll: {title}</h3>
            <div className="soompi-poll-navigation row">
              <div className="col small-6 med-6"><ShowCounter /></div>
              <div className="col small-6 med-6 right-align"><ShowResults/></div>
            </div>
          </div>
          {this.renderByType()}
          <div className={`soompi-poll-navigation-container nav-bottom ${stickyClass}`}>
            <SubmitVotes />
          </div>
        </div>
        <Modal open={this.state.open} alert={this.state.alert} classNames={{overlay: 'modal-alert-overlay'}} onCloseModal={this.close} closeBtn={` `} />
      </React.Fragment>
    )
  }
}

export default Poll



// WEBPACK FOOTER //
// ./assets/js/components/embed/Poll.jsx